import React from "react";
import { Platform } from "react-native";
import { v4 as uuidv4 } from "uuid";

import { SessionContext } from "../../../../../contexts";
import {
  getPatientImages,
  getPatients,
  TPatientInfo,
} from "../../../../../services/api-clinic";
import {
  getAppointmentsByPatient,
  TListAppointmentResponse,
} from "../../../../../services/api-appointment";
import {
  createOutboundCall,
  getCallDetails,
  getPatientNotesForCallDocs,
  hangupCall,
  mintCommunicationsToken,
  resolveSignalWireToken,
  TCommunicationsCall,
  TCommunicationsCallId,
} from "../../../../../services/api-communications";
import { callAudioFeedback } from "../../../../../services/call-audio-feedback";
import {
  describeSessionState,
  getReadableTerminalReason,
  isCallTerminal,
  mapCallToSessionState,
  shouldPollCall,
  TCallSessionState,
} from "../../../../../services/call-session-events";
import {
  clearSignalWireSessionStorage,
  createSignalWireBrowserClient,
  logSignalWireErrorPayload,
  logSignalWireTokenClaimSubset,
} from "../../../../../services/signalwire-browser-auth";
import {
  normalizePatientPhoneForCall,
  phonesLikelyMatch,
} from "../utils/phones";

type TSignalWireInvite = {
  details?: {
    callID?: string;
    caller_id_number?: string;
    callee_id_number?: string;
  };
  accept: (params: { audio?: boolean; video?: boolean }) => Promise<unknown>;
  reject: () => Promise<void>;
};

type TSignalWireIncomingNotification = {
  invite: TSignalWireInvite;
  source?: string | null;
};

type TSignalWireClientLike = {
  online: (params: {
    incomingCallHandlers: {
      all?: (notification: TSignalWireIncomingNotification) => void;
      websocket?: (notification: TSignalWireIncomingNotification) => void;
    };
  }) => Promise<void>;
  offline: () => Promise<void>;
  disconnect: () => Promise<void>;
  updateToken: (token: string) => Promise<void>;
  on?: (eventName: string, handler: (...args: unknown[]) => void) => unknown;
};

type TSignalWireCallLike = {
  on?: (eventName: string, handler: (...args: unknown[]) => void) => unknown;
  once?: (eventName: string, handler: (...args: unknown[]) => void) => unknown;
  off?: (eventName: string, handler: (...args: unknown[]) => void) => unknown;
  getAudioEl?: () => HTMLAudioElement | null;
  hangup?: () => Promise<void>;
  disconnect?: () => Promise<void>;
  leave?: () => Promise<void>;
};

export type TActiveCallContext = {
  callId: TCommunicationsCallId;
  callSid: string;
  clientRequestId: string;
  agentClientIdentity: string;
  fromE164: string;
  toE164: string;
  latestStatus: string;
  latestHangupRequestStatus: string | null;
};

type TStartOutboundCallParams = {
  toE164: string;
  fromE164: string;
  patientUuid?: string;
};

type TIncomingCallerContextStatus = "idle" | "loading" | "ready" | "error";

export type TIncomingCallerContext = {
  status: TIncomingCallerContextStatus;
  patientUuid: string | null;
  patientName: string | null;
  procedures: string[];
  imageCount: number;
  noteCount: number;
  appointmentCount: number;
  nextAppointmentAt: string | null;
  errorMessage: string | null;
};

export type TIncomingCallPrompt = {
  callSid: string | null;
  callerE164: string;
  calleeE164: string;
  source: string | null;
  receivedAtMs: number;
  callerContext: TIncomingCallerContext;
};

type TCallSessionContextValue = {
  state: TCallSessionState;
  stateLabel: string;
  error: string | null;
  activeCall: TCommunicationsCall | null;
  activeCallContext: TActiveCallContext | null;
  isStartingCall: boolean;
  isHangupSubmitting: boolean;
  isMuted: boolean;
  audioWarning: string | null;
  ringingAudioEnabled: boolean;
  connectedDurationSeconds: number;
  incomingCall: TIncomingCallPrompt | null;
  isIncomingActionSubmitting: boolean;
  startOutboundCall: (params: TStartOutboundCallParams) => Promise<void>;
  answerIncomingCall: () => Promise<void>;
  declineIncomingCall: () => Promise<void>;
  requestHangup: () => Promise<void>;
  toggleMute: () => void;
  dismissSummary: () => void;
  retryAudio: () => Promise<void>;
  setRingingAudioEnabled: (enabled: boolean) => void;
};

const CallSessionContext = React.createContext<
  TCallSessionContextValue | undefined
>(undefined);

const CALL_DEBUG_PREFIX = "[CallSessionDebug]";
const SIGNALWIRE_VERBOSE_DEBUG_ENABLED =
  (process.env.EXPO_PUBLIC_SIGNALWIRE_DEBUG || "").trim() === "1";
// Off unless EXPO_PUBLIC_COMMUNICATIONS_ENABLED is set, because the Calls tab is
// still hidden in useHomeLayoutController and every authenticated web session
// mounts this provider — without the gate we mint a SignalWire subscriber token
// per login for a feature nobody can reach. To release: set the env var in the
// target environment AND un-hide the "Conversations" tab; both are required, the
// flag alone leaves incoming calls registered with no way in. Longer term this
// should read a per-clinic capability off the session instead of an env var, so
// pilot clinics can be enabled without a deploy.
const INCOMING_CALL_REGISTRATION_ENABLED = ["1", "true", "yes", "on"].includes(
  (process.env.EXPO_PUBLIC_COMMUNICATIONS_ENABLED || "").trim().toLowerCase(),
);
const POST_ONLINE_CREATE_DELAY_MS = 1500;
const INCOMING_INVITE_TIMEOUT_MS = 20000;
const CREATE_CALL_RESPONSE_TIMEOUT_MS = 30000;
const SIGNALWIRE_EVENT_PROBES = [
  "connected",
  "disconnected",
  "reconnecting",
  "error",
  "invite",
] as const;
const signalWireEventProbeRegistry = new WeakSet<object>();

const isUnknownRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toDebugError = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  return {
    value: String(error),
  };
};

const toDebugValue = (value: unknown): unknown => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return String(value);
  }
};

const logDebugStep = (
  attemptId: string,
  step: string,
  status: "start" | "ok" | "fail" | "info",
  details?: Record<string, unknown>,
) => {
  if (!SIGNALWIRE_VERBOSE_DEBUG_ENABLED) {
    return;
  }

  const payload = details ? { ...details } : undefined;
  if (payload) {
    console.info(
      `${CALL_DEBUG_PREFIX} [${attemptId}] ${step} ${status}`,
      payload,
    );
    return;
  }
  console.info(`${CALL_DEBUG_PREFIX} [${attemptId}] ${step} ${status}`);
};

const delay = (durationMs: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, durationMs);
  });
};

const readNavigatorOnline = (): boolean | null => {
  const browserNavigator = (globalThis as { navigator?: { onLine?: boolean } })
    .navigator;
  if (typeof browserNavigator?.onLine === "boolean") {
    return browserNavigator.onLine;
  }
  return null;
};

const readDocumentVisibilityState = (): string | null => {
  const browserDocument = (
    globalThis as { document?: { visibilityState?: string } }
  ).document;
  if (typeof browserDocument?.visibilityState === "string") {
    return browserDocument.visibilityState;
  }
  return null;
};

const buildLifecycleProbeDetails = (
  callSid: string | null,
  details?: Record<string, unknown>,
): Record<string, unknown> => {
  return {
    callSid,
    navigatorOnLine: readNavigatorOnline(),
    visibilityState: readDocumentVisibilityState(),
    ...(details || {}),
  };
};

const callAttemptIdForHeader = (attemptId: string): string | null => {
  const normalizedAttemptId = (attemptId || "").trim();
  if (!normalizedAttemptId || normalizedAttemptId === "no-attempt") {
    return null;
  }
  return normalizedAttemptId;
};

const resolveSignalWireWsHost = (): string | null => {
  const configuredHost = (
    process.env.EXPO_PUBLIC_SIGNALWIRE_WS_HOST || ""
  ).trim();
  if (!configuredHost) return null;
  if (/^wss?:\/\//i.test(configuredHost)) return configuredHost;
  return `wss://${configuredHost}`;
};

const withTimeout = async <T,>(
  operation: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      operation,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(timeoutMessage));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const isLikelyRetryableNetworkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("timeout") ||
    message.includes("timed out")
  );
};

const parseErrorMessage = (value: string): string => {
  const normalizedValue = (value || "").trim();
  if (!normalizedValue) {
    return "";
  }

  try {
    const parsedValue = JSON.parse(normalizedValue) as
      | string
      | {
          detail?: unknown;
          message?: unknown;
          error?: unknown;
        };
    if (typeof parsedValue === "string") {
      return parsedValue.trim();
    }
    if (typeof parsedValue?.detail === "string") {
      return parsedValue.detail.trim();
    }
    if (typeof parsedValue?.message === "string") {
      return parsedValue.message.trim();
    }
    if (typeof parsedValue?.error === "string") {
      return parsedValue.error.trim();
    }
  } catch {
    // Keep raw text when the payload is not JSON.
  }

  return normalizedValue;
};

const isCommunicationsAccessDisabledError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const statusCode =
    typeof (error as { status?: unknown }).status === "number"
      ? ((error as { status?: number }).status as number)
      : null;
  if (statusCode !== 403) {
    return false;
  }

  const responseMessage = parseErrorMessage(
    String((error as { responseText?: unknown }).responseText || ""),
  ).toLowerCase();
  const errorMessage = parseErrorMessage(
    error instanceof Error ? error.message : "",
  ).toLowerCase();

  return (
    responseMessage.includes("communications access is disabled") ||
    errorMessage.includes("communications access is disabled")
  );
};

const normalizeBrowserCallError = (error: unknown): Error => {
  if (!(error instanceof Error)) {
    return new Error("Failed to place outbound call.");
  }

  const normalizedMessage = error.message.toLowerCase();
  if (
    normalizedMessage.includes("authorization does not provide a subscriber")
  ) {
    return new Error(
      "Browser calling is unavailable because the SignalWire token does not include subscriber authorization. Backend token minting must return a subscriber-capable token for web calls.",
    );
  }

  return error;
};

const extractSignalWireErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (!isUnknownRecord(error)) {
    return "";
  }

  if (typeof error.message === "string") {
    return error.message;
  }

  const nestedError = error.error;
  if (isUnknownRecord(nestedError) && typeof nestedError.message === "string") {
    return nestedError.message;
  }

  return "";
};

const shouldRetrySignalWireRegistration = (error: unknown): boolean => {
  const normalizedMessage = extractSignalWireErrorMessage(error).toLowerCase();
  if (
    normalizedMessage.includes("webrtc endpoint registration failed") ||
    normalizedMessage.includes("requester validation failed") ||
    normalizedMessage.includes("authorization_state")
  ) {
    return true;
  }

  if (isUnknownRecord(error) && typeof error.code === "number") {
    return error.code === -32603 || error.code === -32003;
  }

  return false;
};

const disconnectSignalWireClientBestEffort = async (
  client: unknown,
  options?: {
    attemptId: string;
    callSid: string | null;
  },
): Promise<void> => {
  if (!isUnknownRecord(client)) {
    return;
  }

  const offline = client.offline;
  if (typeof offline === "function") {
    if (options) {
      logDebugStep(
        options.attemptId,
        "signalwire_offline",
        "start",
        buildLifecycleProbeDetails(options.callSid),
      );
    }
    try {
      await (offline as (this: unknown) => Promise<void>).call(client);
      if (options) {
        logDebugStep(
          options.attemptId,
          "signalwire_offline",
          "ok",
          buildLifecycleProbeDetails(options.callSid),
        );
      }
    } catch (error) {
      if (options) {
        logDebugStep(
          options.attemptId,
          "signalwire_offline",
          "fail",
          buildLifecycleProbeDetails(options.callSid, {
            error: toDebugError(error),
          }),
        );
      }
      logSignalWireErrorPayload(
        "SignalWire client offline failed during best-effort cleanup",
        error,
      );
      // Best effort cleanup before retrying with a fresh client.
    }
  }

  const disconnect = client.disconnect;
  if (typeof disconnect === "function") {
    if (options) {
      logDebugStep(
        options.attemptId,
        "signalwire_disconnect",
        "start",
        buildLifecycleProbeDetails(options.callSid),
      );
    }
    try {
      await (disconnect as (this: unknown) => Promise<void>).call(client);
      if (options) {
        logDebugStep(
          options.attemptId,
          "signalwire_disconnect",
          "ok",
          buildLifecycleProbeDetails(options.callSid),
        );
      }
    } catch (error) {
      if (options) {
        logDebugStep(
          options.attemptId,
          "signalwire_disconnect",
          "fail",
          buildLifecycleProbeDetails(options.callSid, {
            error: toDebugError(error),
          }),
        );
      }
      logSignalWireErrorPayload(
        "SignalWire client disconnect failed during best-effort cleanup",
        error,
      );
      // Best effort cleanup before retrying with a fresh client.
    }
  }
};

const e164LooksValid = (value: string): boolean => {
  return /^\+[1-9]\d{7,14}$/.test(value.trim());
};

const SIGNALWIRE_CALL_JOIN_EVENTS = ["call.joined", "room.subscribed"] as const;
const SIGNALWIRE_CALL_TERMINAL_EVENTS = ["destroy", "room.left"] as const;
const SIGNALWIRE_CALL_EVENT_PROBES = [
  ...SIGNALWIRE_CALL_JOIN_EVENTS,
  ...SIGNALWIRE_CALL_TERMINAL_EVENTS,
] as const;

const waitForSignalWireCallJoin = async ({
  call,
  attemptId,
  callSid,
  timeoutMs,
}: {
  call: TSignalWireCallLike;
  attemptId: string;
  callSid: string | null;
  timeoutMs: number;
}): Promise<boolean> => {
  const onHandler = call.on;
  const onceHandler = call.once;
  const offHandler = call.off;

  if (typeof onHandler !== "function" && typeof onceHandler !== "function") {
    logDebugStep(attemptId, "signalwire_call_join_wait", "info", {
      reason: "missing_on_and_once_handlers",
      ...buildLifecycleProbeDetails(callSid),
    });
    return false;
  }

  return new Promise<boolean>((resolve) => {
    let done = false;
    const onSubscriptions: Array<{
      eventName: string;
      handler: (...args: unknown[]) => void;
    }> = [];
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const complete = (fn: () => void) => {
      if (done) return;
      done = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof offHandler === "function") {
        onSubscriptions.forEach(({ eventName, handler }) => {
          try {
            offHandler.call(call, eventName, handler);
          } catch {
            // noop: best-effort cleanup for event listeners.
          }
        });
      }
      fn();
    };

    const subscribe = (
      eventName: string,
      handler: (...args: unknown[]) => void,
    ) => {
      if (typeof onceHandler === "function") {
        onceHandler.call(call, eventName, handler);
        return;
      }
      if (typeof onHandler === "function") {
        onHandler.call(call, eventName, handler);
        onSubscriptions.push({ eventName, handler });
      }
    };

    SIGNALWIRE_CALL_JOIN_EVENTS.forEach((eventName) => {
      subscribe(eventName, (...args: unknown[]) => {
        logDebugStep(attemptId, "signalwire_call_join_wait", "ok", {
          event: eventName,
          args: toDebugValue(args),
          ...buildLifecycleProbeDetails(callSid),
        });
        complete(() => resolve(true));
      });
    });

    SIGNALWIRE_CALL_TERMINAL_EVENTS.forEach((eventName) => {
      subscribe(eventName, (...args: unknown[]) => {
        complete(() => resolve(false));
        logDebugStep(attemptId, "signalwire_call_join_wait", "fail", {
          event: eventName,
          args: toDebugValue(args),
          ...buildLifecycleProbeDetails(callSid),
        });
      });
    });

    timeoutId = setTimeout(() => {
      complete(() => resolve(false));
      logDebugStep(attemptId, "signalwire_call_join_wait", "fail", {
        reason: "join_timeout",
        timeoutMs,
        ...buildLifecycleProbeDetails(callSid),
      });
    }, timeoutMs);
  });
};

const attachSignalWireCallEventProbes = ({
  call,
  attemptId,
  callSid,
}: {
  call: TSignalWireCallLike;
  attemptId: string;
  callSid: string | null;
}) => {
  const onHandler = call.on;
  if (typeof onHandler !== "function") {
    return;
  }

  SIGNALWIRE_CALL_EVENT_PROBES.forEach((eventName) => {
    onHandler.call(call, eventName, (...args: unknown[]) => {
      logDebugStep(attemptId, `signalwire_call_event_${eventName}`, "info", {
        args: toDebugValue(args),
        ...buildLifecycleProbeDetails(callSid),
      });
    });
  });
};

const primeAcceptedCallAudioElement = async ({
  call,
  attemptId,
  callSid,
}: {
  call: TSignalWireCallLike;
  attemptId: string;
  callSid: string | null;
}): Promise<boolean> => {
  const getAudioEl = call.getAudioEl;
  if (typeof getAudioEl !== "function") {
    return true;
  }

  const audioElement = getAudioEl.call(call);
  if (!audioElement) {
    return true;
  }

  audioElement.autoplay = true;
  // @ts-expect-error playsInline is not part of the base HTMLAudioElement type.
  audioElement.playsInline = true;

  if (typeof audioElement.play !== "function") {
    return true;
  }

  try {
    await audioElement.play();
    logDebugStep(attemptId, "signalwire_call_audio_play", "ok", {
      ...buildLifecycleProbeDetails(callSid),
    });
    return true;
  } catch (error) {
    logDebugStep(attemptId, "signalwire_call_audio_play", "fail", {
      error: toDebugError(error),
      ...buildLifecycleProbeDetails(callSid),
    });
    return false;
  }
};

const readConnectedSince = (
  call: TCommunicationsCall | null,
): number | null => {
  if (!call) {
    return null;
  }

  const answeredAt = call.answered_at
    ? new Date(call.answered_at).getTime()
    : NaN;
  if (!Number.isNaN(answeredAt)) {
    return answeredAt;
  }

  const startedAt = call.started_at ? new Date(call.started_at).getTime() : NaN;
  if (!Number.isNaN(startedAt)) {
    return startedAt;
  }

  return Date.now();
};

const EMPTY_INCOMING_CALLER_CONTEXT: TIncomingCallerContext = {
  status: "idle",
  patientUuid: null,
  patientName: null,
  procedures: [],
  imageCount: 0,
  noteCount: 0,
  appointmentCount: 0,
  nextAppointmentAt: null,
  errorMessage: null,
};

const toPatientDisplayName = (patient: TPatientInfo): string => {
  const displayName =
    `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
  if (displayName) {
    return displayName;
  }
  if ((patient.email || "").trim()) {
    return patient.email.trim();
  }
  if ((patient.phone || "").trim()) {
    return patient.phone.trim();
  }
  return "Matched patient";
};

const toNextAppointmentAt = (
  appointments: TListAppointmentResponse[],
): string | null => {
  const now = Date.now();
  const upcoming = appointments
    .map((appointment) => {
      const parsedDate = new Date(appointment.date).getTime();
      if (Number.isNaN(parsedDate)) {
        return null;
      }
      return parsedDate;
    })
    .filter((value): value is number => value !== null)
    .filter((value) => value >= now)
    .sort((left, right) => left - right);

  if (upcoming.length) {
    return new Date(upcoming[0]).toISOString();
  }

  return null;
};

export const CallSessionProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const session = React.useContext(SessionContext)?.session;

  const [state, setState] = React.useState<TCallSessionState>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [activeCall, setActiveCall] =
    React.useState<TCommunicationsCall | null>(null);
  const [activeCallContext, setActiveCallContext] =
    React.useState<TActiveCallContext | null>(null);
  const [isStartingCall, setIsStartingCall] = React.useState(false);
  const [isHangupSubmitting, setIsHangupSubmitting] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [audioWarning, setAudioWarning] = React.useState<string | null>(null);
  const [ringingAudioEnabled, setRingingAudioEnabledState] = React.useState(
    callAudioFeedback.isAudioEnabled(),
  );
  const [connectedDurationSeconds, setConnectedDurationSeconds] =
    React.useState(0);
  const [incomingCall, setIncomingCall] =
    React.useState<TIncomingCallPrompt | null>(null);
  const [isIncomingActionSubmitting, setIsIncomingActionSubmitting] =
    React.useState(false);

  const signalWireClientRef = React.useRef<TSignalWireClientLike | null>(null);
  const activeInviteRef = React.useRef<TSignalWireInvite | null>(null);
  const activeCallSessionRef = React.useRef<TSignalWireCallLike | null>(null);
  const mediaJoinConfirmedRef = React.useRef<boolean>(false);
  const pendingCallAttemptRef = React.useRef<{
    signature: string;
    clientRequestId: string;
  } | null>(null);
  const currentAttemptIdRef = React.useRef<string>("no-attempt");
  const currentCallSidRef = React.useRef<string | null>(null);
  const stateEnteredAtRef = React.useRef<number>(Date.now());
  const inviteWatchdogTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const inviteReceivedRef = React.useRef<boolean>(false);
  const inviteFailureReasonRef = React.useRef<string | null>(null);
  const handledInviteSignaturesRef = React.useRef<Set<string>>(new Set());
  const handledInviteObjectsRef = React.useRef<WeakSet<object>>(new WeakSet());
  const incomingLookupRequestIdRef = React.useRef<number>(0);

  const invokeSignalWireClientMethod = React.useCallback(
    async ({
      attemptId,
      callSid,
      client,
      methodName,
      args = [],
    }: {
      attemptId: string;
      callSid: string | null;
      client: TSignalWireClientLike;
      methodName: "online" | "offline" | "disconnect" | "updateToken";
      args?: unknown[];
    }): Promise<unknown> => {
      const method = client[methodName];
      if (typeof method !== "function") {
        throw new Error(
          `SignalWire client method is unavailable: ${methodName}`,
        );
      }

      logDebugStep(
        attemptId,
        `signalwire_${methodName}`,
        "start",
        buildLifecycleProbeDetails(callSid),
      );
      try {
        const result = await (
          method as (...methodArgs: unknown[]) => Promise<unknown>
        )(...args);
        logDebugStep(
          attemptId,
          `signalwire_${methodName}`,
          "ok",
          buildLifecycleProbeDetails(callSid),
        );
        return result;
      } catch (error) {
        logDebugStep(
          attemptId,
          `signalwire_${methodName}`,
          "fail",
          buildLifecycleProbeDetails(callSid, {
            error: toDebugError(error),
          }),
        );
        logSignalWireErrorPayload(
          `SignalWire client ${methodName} failed`,
          error,
        );
        throw error;
      }
    },
    [],
  );

  const attachSignalWireEventProbes = React.useCallback(
    (client: TSignalWireClientLike) => {
      const clientAsObject = client as unknown as object;
      if (signalWireEventProbeRegistry.has(clientAsObject)) {
        return;
      }

      const onHandler = client.on;
      if (typeof onHandler !== "function") {
        logDebugStep(
          currentAttemptIdRef.current,
          "signalwire_event_probes",
          "info",
          buildLifecycleProbeDetails(currentCallSidRef.current, {
            available: false,
          }),
        );
        signalWireEventProbeRegistry.add(clientAsObject);
        return;
      }

      SIGNALWIRE_EVENT_PROBES.forEach((eventName) => {
        onHandler.call(client, eventName, (...args: unknown[]) => {
          logDebugStep(
            currentAttemptIdRef.current,
            `signalwire_event_${eventName}`,
            "info",
            buildLifecycleProbeDetails(currentCallSidRef.current, {
              args: toDebugValue(args),
            }),
          );
        });
      });

      signalWireEventProbeRegistry.add(clientAsObject);
      logDebugStep(
        currentAttemptIdRef.current,
        "signalwire_event_probes",
        "ok",
        buildLifecycleProbeDetails(currentCallSidRef.current, {
          events: [...SIGNALWIRE_EVENT_PROBES],
        }),
      );
    },
    [],
  );

  const clearInviteWatchdog = React.useCallback(() => {
    const timeoutHandle = inviteWatchdogTimeoutRef.current;
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
      inviteWatchdogTimeoutRef.current = null;
    }
  }, []);

  const resetInviteTracking = React.useCallback(() => {
    clearInviteWatchdog();
    inviteReceivedRef.current = false;
    inviteFailureReasonRef.current = null;
    handledInviteSignaturesRef.current.clear();
    handledInviteObjectsRef.current = new WeakSet();
  }, [clearInviteWatchdog]);

  const markIncomingInviteReceived = React.useCallback(() => {
    inviteReceivedRef.current = true;
    inviteFailureReasonRef.current = null;
    clearInviteWatchdog();
  }, [clearInviteWatchdog]);

  const clearIncomingCallPrompt = React.useCallback(
    (options?: { clearInvite?: boolean }) => {
      incomingLookupRequestIdRef.current += 1;
      setIncomingCall(null);
      setIsIncomingActionSubmitting(false);
      if (options?.clearInvite) {
        activeInviteRef.current = null;
      }
    },
    [],
  );

  const loadIncomingCallerContext = React.useCallback(
    async (callerE164: string) => {
      const normalizedCaller = (callerE164 || "").trim();
      const requestId = incomingLookupRequestIdRef.current + 1;
      incomingLookupRequestIdRef.current = requestId;

      setIncomingCall((currentIncomingCall) => {
        if (!currentIncomingCall) {
          return currentIncomingCall;
        }
        return {
          ...currentIncomingCall,
          callerContext: {
            ...EMPTY_INCOMING_CALLER_CONTEXT,
            status: "loading",
          },
        };
      });

      if (!session || !normalizedCaller) {
        setIncomingCall((currentIncomingCall) => {
          if (
            !currentIncomingCall ||
            incomingLookupRequestIdRef.current !== requestId
          ) {
            return currentIncomingCall;
          }
          return {
            ...currentIncomingCall,
            callerContext: {
              ...EMPTY_INCOMING_CALLER_CONTEXT,
              status: "error",
              errorMessage: !session
                ? "Session is unavailable for patient lookup."
                : "Caller number is unavailable.",
            },
          };
        });
        return;
      }

      try {
        const patientResults = await getPatients(session, {
          phone: normalizedCaller,
          useSubstring: true,
          limit: 25,
        });

        if (incomingLookupRequestIdRef.current !== requestId) {
          return;
        }

        const matchedPatient =
          patientResults.find((patient) =>
            phonesLikelyMatch(
              normalizePatientPhoneForCall(patient.phone),
              normalizedCaller,
            ),
          ) ||
          patientResults.find((patient) => {
            return (
              normalizePatientPhoneForCall(patient.phone).replace(/\D/g, "") ===
              normalizedCaller.replace(/\D/g, "")
            );
          }) ||
          patientResults[0] ||
          null;

        if (!matchedPatient) {
          setIncomingCall((currentIncomingCall) => {
            if (
              !currentIncomingCall ||
              incomingLookupRequestIdRef.current !== requestId
            ) {
              return currentIncomingCall;
            }
            return {
              ...currentIncomingCall,
              callerContext: {
                ...EMPTY_INCOMING_CALLER_CONTEXT,
                status: "ready",
              },
            };
          });
          return;
        }

        const [imagesResult, notesResult, appointmentsResult] =
          await Promise.allSettled([
            getPatientImages(session, matchedPatient.uuid),
            getPatientNotesForCallDocs(session, matchedPatient.uuid),
            getAppointmentsByPatient(session, matchedPatient.uuid),
          ]);

        if (incomingLookupRequestIdRef.current !== requestId) {
          return;
        }

        const images =
          imagesResult.status === "fulfilled" ? imagesResult.value : [];
        const notes =
          notesResult.status === "fulfilled" ? notesResult.value : [];
        const appointments =
          appointmentsResult.status === "fulfilled"
            ? appointmentsResult.value
            : [];

        const procedures = new Set<string>();
        (matchedPatient.proceduresOfInterest || []).forEach((procedure) => {
          const normalizedProcedure = String(procedure || "").trim();
          if (normalizedProcedure) {
            procedures.add(normalizedProcedure);
          }
        });
        appointments.forEach((appointment) => {
          (appointment.unformatted_procedures || []).forEach((procedure) => {
            const normalizedProcedure = String(procedure || "").trim();
            if (normalizedProcedure) {
              procedures.add(normalizedProcedure);
            }
          });
          (appointment.formatted_procedures || []).forEach((procedure) => {
            const normalizedProcedure = String(procedure || "").trim();
            if (normalizedProcedure) {
              procedures.add(normalizedProcedure);
            }
          });
        });

        const hasPartialErrors =
          imagesResult.status === "rejected" ||
          notesResult.status === "rejected" ||
          appointmentsResult.status === "rejected";

        setIncomingCall((currentIncomingCall) => {
          if (
            !currentIncomingCall ||
            incomingLookupRequestIdRef.current !== requestId
          ) {
            return currentIncomingCall;
          }
          return {
            ...currentIncomingCall,
            callerContext: {
              status: "ready",
              patientUuid: matchedPatient.uuid,
              patientName: toPatientDisplayName(matchedPatient),
              procedures: Array.from(procedures),
              imageCount: images.length,
              noteCount: notes.length,
              appointmentCount: appointments.length,
              nextAppointmentAt: toNextAppointmentAt(appointments),
              errorMessage: hasPartialErrors
                ? "Some patient details could not be loaded."
                : null,
            },
          };
        });
      } catch (error) {
        if (incomingLookupRequestIdRef.current !== requestId) {
          return;
        }
        setIncomingCall((currentIncomingCall) => {
          if (!currentIncomingCall) {
            return currentIncomingCall;
          }
          return {
            ...currentIncomingCall,
            callerContext: {
              ...EMPTY_INCOMING_CALLER_CONTEXT,
              status: "error",
              errorMessage:
                error instanceof Error
                  ? error.message
                  : "Unable to load patient details for this caller.",
            },
          };
        });
      }
    },
    [session],
  );

  const acceptIncomingInvite = React.useCallback(
    async ({
      attemptId,
      invite,
    }: {
      attemptId: string;
      invite: TSignalWireInvite;
    }) => {
      setIsIncomingActionSubmitting(true);
      setState("connecting_media");
      logDebugStep(attemptId, "invite_accept", "start");
      try {
        const acceptedCall = (await invite.accept({
          audio: true,
          video: false,
        })) as TSignalWireCallLike;
        activeCallSessionRef.current = acceptedCall;
        attachSignalWireCallEventProbes({
          call: acceptedCall,
          attemptId,
          callSid: currentCallSidRef.current,
        });

        const audioPrimeOk = await primeAcceptedCallAudioElement({
          call: acceptedCall,
          attemptId,
          callSid: currentCallSidRef.current,
        });
        if (!audioPrimeOk) {
          setAudioWarning("Enable sound to hear call audio.");
        }

        const mediaJoinObserved = await waitForSignalWireCallJoin({
          call: acceptedCall,
          attemptId,
          callSid: currentCallSidRef.current,
          timeoutMs: 15000,
        });
        mediaJoinConfirmedRef.current = mediaJoinObserved;
        if (!mediaJoinObserved) {
          logDebugStep(attemptId, "invite_media_join_diagnostic", "info", {
            observed: false,
            ...buildLifecycleProbeDetails(currentCallSidRef.current),
          });
        }
        setState("connected");
        setError(null);
        clearIncomingCallPrompt({ clearInvite: true });
        logDebugStep(attemptId, "invite_accept", "ok");
        logDebugStep(
          attemptId,
          "invite_accepted",
          "ok",
          buildLifecycleProbeDetails(currentCallSidRef.current, {
            clientRequestId: attemptId,
          }),
        );
      } catch (inviteError) {
        mediaJoinConfirmedRef.current = false;
        clearIncomingCallPrompt({ clearInvite: true });
        logDebugStep(attemptId, "invite_accept", "fail", {
          error: toDebugError(inviteError),
        });
        logDebugStep(
          attemptId,
          "invite_accept_failed",
          "fail",
          buildLifecycleProbeDetails(currentCallSidRef.current, {
            clientRequestId: attemptId,
            error: toDebugError(inviteError),
          }),
        );
        setError(
          "Unable to connect browser audio leg. Retry the call to request a fresh token.",
        );
        setState("error");
      } finally {
        setIsIncomingActionSubmitting(false);
      }
    },
    [attachSignalWireCallEventProbes, clearIncomingCallPrompt],
  );

  const armInviteWatchdog = React.useCallback(
    (attemptId: string) => {
      clearInviteWatchdog();
      inviteReceivedRef.current = false;
      inviteFailureReasonRef.current = null;
      inviteWatchdogTimeoutRef.current = setTimeout(() => {
        if (inviteReceivedRef.current) {
          return;
        }
        inviteFailureReasonRef.current = "incoming_invite_timeout";
        logDebugStep(attemptId, "incoming_invite_timeout", "fail", {
          timeoutMs: INCOMING_INVITE_TIMEOUT_MS,
          ...buildLifecycleProbeDetails(currentCallSidRef.current),
        });
        setError(
          `Incoming browser invite timed out after ${Math.floor(INCOMING_INVITE_TIMEOUT_MS / 1000)} seconds.`,
        );
        setState("error");
      }, INCOMING_INVITE_TIMEOUT_MS);
    },
    [clearInviteWatchdog],
  );

  const buildBaseAgentClientIdentity = React.useCallback((): string => {
    if (!session?.clinicId) {
      throw new Error("Missing clinic context for browser calling.");
    }
    if (!session?.userId) {
      throw new Error(
        "Missing authenticated user identity for browser calling.",
      );
    }
    return `clinic:${session.clinicId}:user:${session.userId}`;
  }, [session?.clinicId, session?.userId]);

  const mintBrowserSubscriberToken = React.useCallback(
    async ({
      attemptId,
      agentClientIdentity,
    }: {
      attemptId: string;
      agentClientIdentity: string;
    }): Promise<{
      tokenResponse: Awaited<ReturnType<typeof mintCommunicationsToken>>;
      signalWireToken: string;
      agentClientIdentity: string;
    }> => {
      if (!session) {
        throw new Error("Missing authenticated session.");
      }
      logDebugStep(attemptId, "mint_communications_token", "start", {
        resource: null,
      });
      let tokenResponse: Awaited<ReturnType<typeof mintCommunicationsToken>>;
      try {
        tokenResponse = await mintCommunicationsToken(session, {
          token_type: "browser_subscriber",
          expires_in_minutes: 15,
        });
      } catch (error) {
        if (isCommunicationsAccessDisabledError(error)) {
          throw new Error(
            "Ask your clinic admin to enable Communications access.",
          );
        }
        throw error;
      }
      logDebugStep(attemptId, "mint_communications_token", "ok", {
        project: tokenResponse.project,
        expiresInMinutes: tokenResponse.expires_in_minutes,
        resource: null,
      });

      const signalWireToken = resolveSignalWireToken(tokenResponse);
      logSignalWireTokenClaimSubset(signalWireToken);
      return {
        tokenResponse,
        signalWireToken,
        agentClientIdentity,
      };
    },
    [session],
  );

  const ensureBrowserLegReady = React.useCallback(
    async (options?: {
      prepareMedia?: boolean;
      updateState?: boolean;
    }): Promise<string> => {
      const attemptId = currentAttemptIdRef.current;
      const shouldPrepareMedia = options?.prepareMedia !== false;
      const shouldUpdateState = options?.updateState !== false;

      logDebugStep(attemptId, "ensure_browser_leg_ready", "start", {
        platform: Platform.OS,
        hasSession: Boolean(session),
        shouldPrepareMedia,
      });

      if (!session) {
        logDebugStep(attemptId, "ensure_browser_leg_ready", "fail", {
          reason: "missing_session",
        });
        throw new Error("Missing authenticated session.");
      }
      if (Platform.OS !== "web") {
        logDebugStep(attemptId, "ensure_browser_leg_ready", "fail", {
          reason: "non_web_platform",
          platform: Platform.OS,
        });
        throw new Error(
          "Browser calling mode is currently supported on web only.",
        );
      }

      const browserNavigator =
        typeof globalThis !== "undefined"
          ? ((globalThis as { navigator?: Navigator }).navigator ?? null)
          : null;

      if (shouldPrepareMedia) {
        if (
          !browserNavigator ||
          !browserNavigator.mediaDevices ||
          !browserNavigator.mediaDevices.getUserMedia
        ) {
          logDebugStep(attemptId, "ensure_browser_leg_ready", "fail", {
            reason: "browser_media_api_unavailable",
          });
          throw new Error(
            "This browser does not support microphone access for web calling.",
          );
        }

        await callAudioFeedback.primeAudio().catch(() => {
          logDebugStep(attemptId, "prime_audio", "fail", {
            reason: "autoplay_or_context_resume_failed",
          });
          setAudioWarning("Enable sound to hear ringing.");
        });

        if (shouldUpdateState) {
          setState("preparing_media");
        }
        logDebugStep(attemptId, "get_user_media_probe", "start");

        try {
          const mediaStream = await browserNavigator.mediaDevices.getUserMedia({
            audio: true,
          });
          const trackCount = mediaStream.getTracks().length;
          logDebugStep(attemptId, "get_user_media_probe", "ok", {
            trackCount,
          });
          mediaStream.getTracks().forEach((track) => track.stop());
        } catch (error) {
          logDebugStep(attemptId, "get_user_media_probe", "fail", {
            error: toDebugError(error),
          });
          const errorName =
            typeof error === "object" && error !== null && "name" in error
              ? String((error as { name?: string }).name)
              : "";
          if (
            errorName === "NotAllowedError" ||
            errorName === "PermissionDeniedError"
          ) {
            throw new Error(
              "Microphone access is blocked. Allow microphone permissions in your browser settings and try again.",
            );
          }
          throw error;
        }
      }

      if (shouldUpdateState) {
        setState("registering_client");
      }
      const agentClientIdentity = buildBaseAgentClientIdentity();
      const mintedTokenResult = await mintBrowserSubscriberToken({
        attemptId,
        agentClientIdentity,
      });
      let tokenResponse = mintedTokenResult.tokenResponse;
      let signalWireToken = mintedTokenResult.signalWireToken;
      let resolvedAgentClientIdentity = mintedTokenResult.agentClientIdentity;

      const existingClient = signalWireClientRef.current;
      const signalWireWsHost = resolveSignalWireWsHost();

      const handleIncomingInvite = async (
        notification: TSignalWireIncomingNotification,
        handlerSource: "all" | "websocket",
      ) => {
        const source = (notification.source || "").trim().toLowerCase();
        if (handlerSource === "all" && source === "websocket") {
          return;
        }

        const inviteObject = notification.invite as unknown;
        if (
          isUnknownRecord(inviteObject) &&
          handledInviteObjectsRef.current.has(inviteObject)
        ) {
          logDebugStep(attemptId, "incoming_invite_deduped", "info", {
            handlerSource,
            reason: "same_invite_object",
            ...buildLifecycleProbeDetails(currentCallSidRef.current),
          });
          return;
        }
        if (isUnknownRecord(inviteObject)) {
          handledInviteObjectsRef.current.add(inviteObject);
        }

        const inviteDetails = notification.invite?.details || {};
        const inviteSignature = [
          inviteDetails.callID || "",
          inviteDetails.caller_id_number || "",
          inviteDetails.callee_id_number || "",
        ].join("|");
        if (
          inviteSignature &&
          handledInviteSignaturesRef.current.has(inviteSignature)
        ) {
          logDebugStep(attemptId, "incoming_invite_deduped", "info", {
            handlerSource,
            source: notification.source || null,
            inviteSignature,
            ...buildLifecycleProbeDetails(currentCallSidRef.current),
          });
          return;
        }
        if (inviteSignature) {
          handledInviteSignaturesRef.current.add(inviteSignature);
        }

        const isOutboundFlowInvite = Boolean(inviteWatchdogTimeoutRef.current);
        const inviteAttemptId = isOutboundFlowInvite
          ? currentAttemptIdRef.current
          : (inviteDetails.callID || "").trim() || uuidv4();
        currentAttemptIdRef.current = inviteAttemptId;
        const inviteCallSid = (inviteDetails.callID || "").trim();
        if (inviteCallSid) {
          currentCallSidRef.current = inviteCallSid;
        }

        logDebugStep(inviteAttemptId, "incoming_invite_received", "info", {
          handlerSource,
          source: notification.source || null,
          callID: inviteDetails.callID || null,
          ...buildLifecycleProbeDetails(currentCallSidRef.current),
        });
        markIncomingInviteReceived();
        activeInviteRef.current = notification.invite;

        if (!isOutboundFlowInvite && activeCallSessionRef.current) {
          logDebugStep(
            inviteAttemptId,
            "incoming_invite_rejected_busy",
            "info",
            {
              reason: "active_call_session_present",
            },
          );
          await notification.invite.reject().catch(() => undefined);
          return;
        }

        if (isOutboundFlowInvite) {
          await acceptIncomingInvite({
            attemptId: inviteAttemptId,
            invite: notification.invite,
          });
          return;
        }

        setError(null);
        setAudioWarning(null);
        setIsIncomingActionSubmitting(false);
        setIncomingCall({
          callSid: inviteCallSid || null,
          callerE164: (inviteDetails.caller_id_number || "").trim(),
          calleeE164: (inviteDetails.callee_id_number || "").trim(),
          source: notification.source || null,
          receivedAtMs: Date.now(),
          callerContext: {
            ...EMPTY_INCOMING_CALLER_CONTEXT,
            status: "loading",
          },
        });
        setState("incoming_ringing");

        loadIncomingCallerContext(
          (inviteDetails.caller_id_number || "").trim(),
        ).catch(() => undefined);
      };

      const incomingCallHandlers = {
        websocket: async (notification: TSignalWireIncomingNotification) => {
          await handleIncomingInvite(notification, "websocket");
        },
        all: async (notification: TSignalWireIncomingNotification) => {
          await handleIncomingInvite(notification, "all");
        },
      };

      if (existingClient) {
        attachSignalWireEventProbes(existingClient);
        logDebugStep(
          attemptId,
          "client_created",
          "info",
          buildLifecycleProbeDetails(currentCallSidRef.current, {
            clientRequestId: attemptId,
            reusedExistingClient: true,
          }),
        );
        try {
          await withTimeout(
            invokeSignalWireClientMethod({
              attemptId,
              callSid: currentCallSidRef.current,
              client: existingClient,
              methodName: "updateToken",
              args: [signalWireToken],
            }),
            15000,
            "SignalWire token update timed out. Verify SignalWire credentials and websocket connectivity.",
          );

          logDebugStep(attemptId, "signalwire_online_existing_client", "start");
          await withTimeout(
            invokeSignalWireClientMethod({
              attemptId,
              callSid: currentCallSidRef.current,
              client: existingClient,
              methodName: "online",
              args: [{ incomingCallHandlers }],
            }),
            15000,
            "SignalWire client registration timed out. If this persists, check SignalWire project credentials and allowed origins.",
          );
          logDebugStep(attemptId, "signalwire_online_existing_client", "ok");
          logDebugStep(
            attemptId,
            "client_online_registered",
            "ok",
            buildLifecycleProbeDetails(currentCallSidRef.current, {
              clientRequestId: attemptId,
              reusedExistingClient: true,
              retry: false,
            }),
          );
        } catch (error) {
          const shouldRetry = shouldRetrySignalWireRegistration(error);
          if (shouldRetry) {
            logDebugStep(
              attemptId,
              "signalwire_online_existing_client_retry",
              "start",
              {
                error: toDebugError(error),
              },
            );
            clearSignalWireSessionStorage();
            await disconnectSignalWireClientBestEffort(existingClient, {
              attemptId,
              callSid: currentCallSidRef.current,
            });
            signalWireClientRef.current = null;

            try {
              const retryAgentClientIdentity = buildBaseAgentClientIdentity();
              const retryMintedTokenResult = await mintBrowserSubscriberToken({
                attemptId,
                agentClientIdentity: retryAgentClientIdentity,
              });
              tokenResponse = retryMintedTokenResult.tokenResponse;
              signalWireToken = retryMintedTokenResult.signalWireToken;
              resolvedAgentClientIdentity =
                retryMintedTokenResult.agentClientIdentity;

              logDebugStep(
                attemptId,
                "signalwire_retry_create_client",
                "start",
                {
                  hasCustomHost: Boolean(signalWireWsHost),
                },
              );
              const retryClient = await withTimeout(
                createSignalWireBrowserClient({
                  host: signalWireWsHost,
                  project: tokenResponse.project,
                  token: signalWireToken,
                }),
                15000,
                "SignalWire client initialization timed out. Verify websocket auth settings for this host.",
              );
              logDebugStep(attemptId, "signalwire_retry_create_client", "ok");
              const retryClientTyped = retryClient as TSignalWireClientLike;
              attachSignalWireEventProbes(retryClientTyped);

              logDebugStep(
                attemptId,
                "signalwire_retry_online_new_client",
                "start",
              );
              await withTimeout(
                invokeSignalWireClientMethod({
                  attemptId,
                  callSid: currentCallSidRef.current,
                  client: retryClientTyped,
                  methodName: "online",
                  args: [{ incomingCallHandlers }],
                }),
                15000,
                "SignalWire websocket registration timed out. Check SignalWire account/browser auth restrictions.",
              );
              logDebugStep(
                attemptId,
                "signalwire_retry_online_new_client",
                "ok",
              );
              logDebugStep(
                attemptId,
                "client_online_registered",
                "ok",
                buildLifecycleProbeDetails(currentCallSidRef.current, {
                  clientRequestId: attemptId,
                  reusedExistingClient: true,
                  retry: true,
                }),
              );

              signalWireClientRef.current = retryClientTyped;
              logDebugStep(
                attemptId,
                "signalwire_online_existing_client_retry",
                "ok",
              );
              return agentClientIdentity;
            } catch (retryError) {
              logDebugStep(
                attemptId,
                "signalwire_online_existing_client_retry",
                "fail",
                {
                  error: toDebugError(retryError),
                },
              );
              logSignalWireErrorPayload(
                "SignalWire retry registration failed after existing client online failure",
                retryError,
              );
              throw normalizeBrowserCallError(retryError);
            }
          }

          logDebugStep(attemptId, "signalwire_online_existing_client", "fail", {
            error: toDebugError(error),
          });
          logSignalWireErrorPayload(
            "SignalWire token refresh or online registration failed",
            error,
          );
          throw normalizeBrowserCallError(error);
        }

        return agentClientIdentity;
      }

      let nextClient: unknown;
      try {
        logDebugStep(attemptId, "signalwire_create_client", "start", {
          hasCustomHost: Boolean(signalWireWsHost),
        });
        nextClient = await withTimeout(
          createSignalWireBrowserClient({
            host: signalWireWsHost,
            project: tokenResponse.project,
            token: signalWireToken,
          }),
          15000,
          "SignalWire client initialization timed out. Verify websocket auth settings for this host.",
        );
        logDebugStep(attemptId, "signalwire_create_client", "ok");
        logDebugStep(
          attemptId,
          "client_created",
          "ok",
          buildLifecycleProbeDetails(currentCallSidRef.current, {
            clientRequestId: attemptId,
            reusedExistingClient: false,
            retry: false,
          }),
        );
      } catch (error) {
        logDebugStep(attemptId, "signalwire_create_client", "fail", {
          error: toDebugError(error),
        });
        logSignalWireErrorPayload(
          "SignalWire client initialization failed",
          error,
        );
        throw error;
      }
      const nextClientTyped = nextClient as TSignalWireClientLike;
      attachSignalWireEventProbes(nextClientTyped);

      try {
        logDebugStep(attemptId, "signalwire_online_new_client", "start");
        await withTimeout(
          invokeSignalWireClientMethod({
            attemptId,
            callSid: currentCallSidRef.current,
            client: nextClientTyped,
            methodName: "online",
            args: [{ incomingCallHandlers }],
          }),
          15000,
          "SignalWire websocket registration timed out. Check SignalWire account/browser auth restrictions.",
        );
        logDebugStep(attemptId, "signalwire_online_new_client", "ok");
        logDebugStep(
          attemptId,
          "client_online_registered",
          "ok",
          buildLifecycleProbeDetails(currentCallSidRef.current, {
            clientRequestId: attemptId,
            reusedExistingClient: false,
            retry: false,
          }),
        );
      } catch (error) {
        const shouldRetry = shouldRetrySignalWireRegistration(error);
        if (shouldRetry) {
          logDebugStep(
            attemptId,
            "signalwire_online_new_client_retry",
            "start",
            {
              error: toDebugError(error),
            },
          );
          clearSignalWireSessionStorage();
          await disconnectSignalWireClientBestEffort(nextClient, {
            attemptId,
            callSid: currentCallSidRef.current,
          });
          signalWireClientRef.current = null;

          try {
            const retryAgentClientIdentity = buildBaseAgentClientIdentity();
            const retryMintedTokenResult = await mintBrowserSubscriberToken({
              attemptId,
              agentClientIdentity: retryAgentClientIdentity,
            });
            tokenResponse = retryMintedTokenResult.tokenResponse;
            signalWireToken = retryMintedTokenResult.signalWireToken;
            resolvedAgentClientIdentity =
              retryMintedTokenResult.agentClientIdentity;

            logDebugStep(attemptId, "signalwire_retry_create_client", "start", {
              hasCustomHost: Boolean(signalWireWsHost),
            });
            const retryClient = await withTimeout(
              createSignalWireBrowserClient({
                host: signalWireWsHost,
                project: tokenResponse.project,
                token: signalWireToken,
              }),
              15000,
              "SignalWire client initialization timed out. Verify websocket auth settings for this host.",
            );
            logDebugStep(attemptId, "signalwire_retry_create_client", "ok");
            logDebugStep(
              attemptId,
              "client_created",
              "ok",
              buildLifecycleProbeDetails(currentCallSidRef.current, {
                clientRequestId: attemptId,
                reusedExistingClient: false,
                retry: true,
              }),
            );
            const retryClientTyped = retryClient as TSignalWireClientLike;
            attachSignalWireEventProbes(retryClientTyped);

            logDebugStep(
              attemptId,
              "signalwire_retry_online_new_client",
              "start",
            );
            await withTimeout(
              invokeSignalWireClientMethod({
                attemptId,
                callSid: currentCallSidRef.current,
                client: retryClientTyped,
                methodName: "online",
                args: [{ incomingCallHandlers }],
              }),
              15000,
              "SignalWire websocket registration timed out. Check SignalWire account/browser auth restrictions.",
            );
            logDebugStep(attemptId, "signalwire_retry_online_new_client", "ok");
            logDebugStep(
              attemptId,
              "client_online_registered",
              "ok",
              buildLifecycleProbeDetails(currentCallSidRef.current, {
                clientRequestId: attemptId,
                reusedExistingClient: false,
                retry: true,
              }),
            );

            signalWireClientRef.current = retryClientTyped;
            logDebugStep(attemptId, "signalwire_online_new_client_retry", "ok");
            return agentClientIdentity;
          } catch (retryError) {
            logDebugStep(
              attemptId,
              "signalwire_online_new_client_retry",
              "fail",
              {
                error: toDebugError(retryError),
              },
            );
            logSignalWireErrorPayload(
              "SignalWire retry registration failed after new client online failure",
              retryError,
            );
            throw normalizeBrowserCallError(retryError);
          }
        }

        logDebugStep(attemptId, "signalwire_online_new_client", "fail", {
          error: toDebugError(error),
        });
        logSignalWireErrorPayload(
          "SignalWire websocket online registration failed",
          error,
        );
        throw normalizeBrowserCallError(error);
      }

      signalWireClientRef.current = nextClientTyped;
      logDebugStep(attemptId, "ensure_browser_leg_ready", "ok");
      return resolvedAgentClientIdentity;
    },
    [
      acceptIncomingInvite,
      attachSignalWireEventProbes,
      buildBaseAgentClientIdentity,
      invokeSignalWireClientMethod,
      loadIncomingCallerContext,
      markIncomingInviteReceived,
      mintBrowserSubscriberToken,
      session,
    ],
  );

  React.useEffect(() => {
    currentCallSidRef.current = activeCallContext?.callSid ?? null;
  }, [activeCallContext?.callSid]);

  React.useEffect(() => {
    if (
      !INCOMING_CALL_REGISTRATION_ENABLED ||
      !session ||
      Platform.OS !== "web"
    ) {
      return;
    }

    let isDisposed = false;
    const previousAttemptId = currentAttemptIdRef.current;
    if (previousAttemptId === "no-attempt") {
      currentAttemptIdRef.current = `incoming-ready-${uuidv4()}`;
    }
    const warmupAttemptId = currentAttemptIdRef.current;

    ensureBrowserLegReady({
      prepareMedia: false,
      updateState: false,
    }).catch((error) => {
      if (isDisposed) {
        return;
      }
      logDebugStep(warmupAttemptId, "incoming_registration_warmup", "fail", {
        error: toDebugError(error),
      });
    });

    return () => {
      isDisposed = true;
    };
  }, [ensureBrowserLegReady, session?.clinicId, session?.userId]);

  React.useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    const browserWindow = (
      globalThis as {
        window?: {
          addEventListener?: (eventName: string, listener: () => void) => void;
          removeEventListener?: (
            eventName: string,
            listener: () => void,
          ) => void;
        };
      }
    ).window;
    const browserDocument = (
      globalThis as {
        document?: {
          addEventListener?: (eventName: string, listener: () => void) => void;
          removeEventListener?: (
            eventName: string,
            listener: () => void,
          ) => void;
        };
      }
    ).document;

    if (
      !browserWindow ||
      typeof browserWindow.addEventListener !== "function" ||
      typeof browserWindow.removeEventListener !== "function"
    ) {
      return;
    }
    const addWindowEventListener =
      browserWindow.addEventListener.bind(browserWindow);
    const removeWindowEventListener =
      browserWindow.removeEventListener.bind(browserWindow);

    const logWindowEvent = (eventName: string) => {
      logDebugStep(
        currentAttemptIdRef.current,
        `window_${eventName}`,
        "info",
        buildLifecycleProbeDetails(currentCallSidRef.current),
      );
    };

    const handleOnline = () => logWindowEvent("online");
    const handleOffline = () => logWindowEvent("offline");
    const handleBeforeUnload = () => logWindowEvent("beforeunload");
    const handlePageHide = () => logWindowEvent("pagehide");
    const handleVisibilityChange = () => logWindowEvent("visibilitychange");

    addWindowEventListener("online", handleOnline);
    addWindowEventListener("offline", handleOffline);
    addWindowEventListener("beforeunload", handleBeforeUnload);
    addWindowEventListener("pagehide", handlePageHide);
    browserDocument?.addEventListener?.(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      removeWindowEventListener("online", handleOnline);
      removeWindowEventListener("offline", handleOffline);
      removeWindowEventListener("beforeunload", handleBeforeUnload);
      removeWindowEventListener("pagehide", handlePageHide);
      browserDocument?.removeEventListener?.(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, []);

  const syncCallDetails = React.useCallback(async () => {
    const attemptId = currentAttemptIdRef.current;
    if (!session || !activeCallContext?.callId) {
      return;
    }

    logDebugStep(attemptId, "sync_call_details", "start", {
      callId: activeCallContext.callId,
    });
    const detailResponse = await getCallDetails(
      session,
      activeCallContext.callId,
      {
        callAttemptId:
          activeCallContext.clientRequestId ||
          callAttemptIdForHeader(currentAttemptIdRef.current),
      },
    );
    const call = detailResponse.call;
    logDebugStep(attemptId, "sync_call_details", "ok", {
      callId: call.call_uuid,
      status: call.status,
      statusReason: call.status_reason,
      hangupRequestStatus: call.hangup_request_status,
    });

    setActiveCall(call);
    setActiveCallContext((current) => {
      if (!current || current.callId !== call.call_uuid) {
        return current;
      }
      return {
        ...current,
        latestStatus: call.status,
        latestHangupRequestStatus: call.hangup_request_status,
      };
    });

    const callIsTerminal = isCallTerminal(call.status);
    if (callIsTerminal && !inviteReceivedRef.current) {
      const terminalBeforeInviteMessage = `Call reached terminal status (${call.status}) before browser invite was received.`;
      inviteFailureReasonRef.current = `call_terminated_before_invite:${call.status}`;
      clearInviteWatchdog();
      logDebugStep(attemptId, "call_terminated_before_invite", "fail", {
        status: call.status,
        ...buildLifecycleProbeDetails(call.call_sid),
      });
      setError(terminalBeforeInviteMessage);
    }

    setState((currentState) => {
      if (inviteFailureReasonRef.current && !inviteReceivedRef.current) {
        return "error";
      }
      if (callIsTerminal && !inviteReceivedRef.current) {
        return "error";
      }
      return mapCallToSessionState(currentState, call);
    });

    if (callIsTerminal) {
      activeInviteRef.current = null;
      activeCallSessionRef.current = null;
      mediaJoinConfirmedRef.current = false;
      clearIncomingCallPrompt();
    }
  }, [
    activeCallContext?.callId,
    activeCallContext?.clientRequestId,
    clearIncomingCallPrompt,
    clearInviteWatchdog,
    session,
  ]);

  const startOutboundCall = React.useCallback(
    async ({ toE164, fromE164, patientUuid }: TStartOutboundCallParams) => {
      if (!session) {
        return;
      }

      setError(null);
      setAudioWarning(null);
      clearIncomingCallPrompt({ clearInvite: true });

      if (!e164LooksValid(toE164)) {
        setError(
          "Patient number must be in E.164 format, for example +14155550123.",
        );
        setState("error");
        return;
      }

      if (!e164LooksValid(fromE164)) {
        setError("From number must be in E.164 format.");
        setState("error");
        return;
      }

      const normalizedTo = toE164.trim();
      const normalizedFrom = fromE164.trim();
      const normalizedPatientUuid = patientUuid?.trim() || "";

      const attemptSignature = `${normalizedTo}|${normalizedFrom}|${normalizedPatientUuid}`;
      const previousAttempt = pendingCallAttemptRef.current;
      const clientRequestId =
        previousAttempt && previousAttempt.signature === attemptSignature
          ? previousAttempt.clientRequestId
          : uuidv4();
      const attemptId = clientRequestId;
      currentAttemptIdRef.current = attemptId;
      mediaJoinConfirmedRef.current = false;
      resetInviteTracking();
      logDebugStep(attemptId, "start_outbound_call", "start", {
        toE164: normalizedTo,
        fromE164: normalizedFrom,
        hasPatientUuid: Boolean(normalizedPatientUuid),
      });

      pendingCallAttemptRef.current = {
        signature: attemptSignature,
        clientRequestId,
      };

      let createRequestSent = false;
      setIsStartingCall(true);

      try {
        const agentClientIdentity = await ensureBrowserLegReady();
        logDebugStep(attemptId, "agent_identity_ready", "ok", {
          agentClientIdentity,
        });
        logDebugStep(attemptId, "post_online_delay_start", "start", {
          delayMs: POST_ONLINE_CREATE_DELAY_MS,
        });
        await delay(POST_ONLINE_CREATE_DELAY_MS);
        logDebugStep(attemptId, "post_online_delay_ok", "ok", {
          delayMs: POST_ONLINE_CREATE_DELAY_MS,
        });

        setState("outbound_creating");
        armInviteWatchdog(attemptId);

        const createPayload = {
          client_request_id: clientRequestId,
          to_e164: normalizedTo,
          from_e164: normalizedFrom,
          patient_uuid: normalizedPatientUuid || undefined,
          agent_client_identity: agentClientIdentity,
        };

        const requestCreateCall = async () => {
          createRequestSent = true;
          logDebugStep(attemptId, "create_outbound_call", "start", {
            payload: {
              ...createPayload,
              patient_uuid: createPayload.patient_uuid || null,
            },
          });
          return withTimeout(
            createOutboundCall(session, createPayload),
            CREATE_CALL_RESPONSE_TIMEOUT_MS,
            `Create call response timed out after ${CREATE_CALL_RESPONSE_TIMEOUT_MS}ms.`,
          );
        };

        let response;
        try {
          response = await requestCreateCall();
          logDebugStep(attemptId, "create_outbound_call", "ok", {
            callId: response.call_uuid,
            callSid: response.call_sid,
            status: response.status,
            retry: false,
          });
        } catch (error) {
          logDebugStep(attemptId, "create_outbound_call", "fail", {
            retrying: isLikelyRetryableNetworkError(error),
            error: toDebugError(error),
          });
          if (!isLikelyRetryableNetworkError(error)) {
            throw error;
          }
          response = await requestCreateCall();
          logDebugStep(attemptId, "create_outbound_call", "ok", {
            callId: response.call_uuid,
            callSid: response.call_sid,
            status: response.status,
            retry: true,
          });
        }

        pendingCallAttemptRef.current = null;

        setActiveCallContext({
          callId: response.call_uuid,
          callSid: response.call_sid,
          clientRequestId,
          agentClientIdentity,
          fromE164: normalizedFrom,
          toE164: normalizedTo,
          latestStatus: response.status,
          latestHangupRequestStatus: null,
        });

        setState(
          response.status.toLowerCase() === "ringing"
            ? "outbound_ringing"
            : "outbound_creating",
        );

        await syncCallDetails();
        logDebugStep(attemptId, "start_outbound_call", "ok");
      } catch (error) {
        const normalizedError = normalizeBrowserCallError(error);
        logDebugStep(attemptId, "start_outbound_call", "fail", {
          error: toDebugError(normalizedError),
          createRequestSent,
        });
        clearInviteWatchdog();
        setError(normalizedError.message || "Failed to place outbound call.");

        if (!createRequestSent) {
          pendingCallAttemptRef.current = null;
        }

        setState("error");
      } finally {
        setIsStartingCall(false);
        logDebugStep(attemptId, "start_outbound_call", "info", {
          done: true,
          finalState: state,
        });
      }
    },
    [
      armInviteWatchdog,
      clearIncomingCallPrompt,
      clearInviteWatchdog,
      ensureBrowserLegReady,
      resetInviteTracking,
      session,
      state,
      syncCallDetails,
    ],
  );

  const answerIncomingCall = React.useCallback(async () => {
    const invite = activeInviteRef.current;
    if (!invite) {
      return;
    }

    const attemptId =
      currentAttemptIdRef.current === "no-attempt"
        ? uuidv4()
        : currentAttemptIdRef.current;
    currentAttemptIdRef.current = attemptId;
    await acceptIncomingInvite({
      attemptId,
      invite,
    });
  }, [acceptIncomingInvite]);

  const declineIncomingCall = React.useCallback(async () => {
    const invite = activeInviteRef.current;
    const attemptId =
      currentAttemptIdRef.current === "no-attempt"
        ? uuidv4()
        : currentAttemptIdRef.current;
    currentAttemptIdRef.current = attemptId;

    setIsIncomingActionSubmitting(true);
    setError(null);
    try {
      if (invite) {
        await invite.reject();
      }
      logDebugStep(attemptId, "invite_decline", "ok");
    } catch (error) {
      logDebugStep(attemptId, "invite_decline", "fail", {
        error: toDebugError(error),
      });
      setError(
        error instanceof Error
          ? error.message
          : "Unable to decline incoming call right now.",
      );
    } finally {
      clearIncomingCallPrompt({ clearInvite: true });
      setState((currentState) =>
        currentState === "incoming_ringing" ? "idle" : currentState,
      );
      setIsIncomingActionSubmitting(false);
    }
  }, [clearIncomingCallPrompt]);

  const requestHangup = React.useCallback(async () => {
    const attemptId = currentAttemptIdRef.current;
    const activeCallSession = activeCallSessionRef.current;
    if (!activeCallContext?.callId && !activeCallSession) {
      return;
    }

    setIsHangupSubmitting(true);
    setError(null);
    setState("ending");
    logDebugStep(attemptId, "hangup_request", "start", {
      callId: activeCallContext?.callId || null,
      sdkFallback: !activeCallContext?.callId,
    });

    if (!activeCallContext?.callId) {
      try {
        if (typeof activeCallSession?.hangup === "function") {
          await activeCallSession.hangup();
        } else if (typeof activeCallSession?.disconnect === "function") {
          await activeCallSession.disconnect();
        } else if (typeof activeCallSession?.leave === "function") {
          await activeCallSession.leave();
        } else {
          throw new Error("Hangup control is unavailable for this call.");
        }
        logDebugStep(attemptId, "hangup_request", "ok", {
          callId: null,
          source: "sdk",
        });
        activeInviteRef.current = null;
        activeCallSessionRef.current = null;
        mediaJoinConfirmedRef.current = false;
        setState("ended");
      } catch (hangupError) {
        logDebugStep(attemptId, "hangup_request", "fail", {
          error: toDebugError(hangupError),
          source: "sdk",
        });
        setError(
          hangupError instanceof Error
            ? hangupError.message
            : "Failed to end the in-browser call.",
        );
        setState("error");
      } finally {
        setIsHangupSubmitting(false);
      }
      return;
    }

    if (!session) {
      setIsHangupSubmitting(false);
      setError("Missing authenticated session.");
      setState("error");
      return;
    }

    try {
      const response = await hangupCall(session, activeCallContext.callId, {
        callAttemptId:
          activeCallContext.clientRequestId ||
          callAttemptIdForHeader(currentAttemptIdRef.current),
      });
      logDebugStep(attemptId, "hangup_request", "ok", {
        callId: response.call_uuid,
        status: response.status,
        hangupRequestStatus: response.hangup_request_status,
      });
      setActiveCallContext((current) => {
        if (!current || current.callId !== response.call_uuid) {
          return current;
        }
        return {
          ...current,
          latestStatus: response.status,
          latestHangupRequestStatus: response.hangup_request_status,
        };
      });
      await syncCallDetails();
    } catch (hangupError) {
      logDebugStep(attemptId, "hangup_request", "fail", {
        error: toDebugError(hangupError),
      });
      setError(
        hangupError instanceof Error
          ? hangupError.message
          : "Failed to request call hangup.",
      );
      setState("error");
    } finally {
      setIsHangupSubmitting(false);
    }
  }, [
    activeCallContext?.callId,
    activeCallContext?.clientRequestId,
    session,
    syncCallDetails,
  ]);

  const toggleMute = React.useCallback(() => {
    // TODO: replace with SDK-native mute once call object controls are wired.
    setIsMuted((current) => !current);
    logDebugStep(currentAttemptIdRef.current, "toggle_mute", "info", {
      requested: true,
    });
  }, []);

  const dismissSummary = React.useCallback(() => {
    logDebugStep(currentAttemptIdRef.current, "dismiss_summary", "info");
    callAudioFeedback.stopAll();
    resetInviteTracking();
    clearIncomingCallPrompt({ clearInvite: true });
    setState("idle");
    setError(null);
    setAudioWarning(null);
    setConnectedDurationSeconds(0);
    setIsMuted(false);
    setActiveCall(null);
    setActiveCallContext(null);
    activeInviteRef.current = null;
    activeCallSessionRef.current = null;
    mediaJoinConfirmedRef.current = false;
  }, [clearIncomingCallPrompt, resetInviteTracking]);

  const retryAudio = React.useCallback(async () => {
    try {
      logDebugStep(currentAttemptIdRef.current, "retry_audio", "start");
      await callAudioFeedback.primeAudio();
      setAudioWarning(null);
      if (state === "outbound_ringing" || state === "incoming_ringing") {
        await callAudioFeedback.playRingback();
      }
      logDebugStep(currentAttemptIdRef.current, "retry_audio", "ok");
    } catch {
      logDebugStep(currentAttemptIdRef.current, "retry_audio", "fail");
      setAudioWarning("Enable sound to hear ringing.");
    }
  }, [state]);

  const setRingingAudioEnabled = React.useCallback((enabled: boolean) => {
    callAudioFeedback.setAudioEnabled(enabled);
    setRingingAudioEnabledState(enabled);
    logDebugStep(
      currentAttemptIdRef.current,
      "ringing_audio_preference",
      "info",
      {
        enabled,
      },
    );
    if (!enabled) {
      callAudioFeedback.stopRingback();
    }
  }, []);

  React.useEffect(() => {
    const previousState = state;
    const enteredAt = Date.now();
    const elapsedMs = enteredAt - stateEnteredAtRef.current;
    logDebugStep(currentAttemptIdRef.current, "state_transition", "info", {
      state: previousState,
      elapsedSinceLastStateMs: elapsedMs,
      activeCallId: activeCall?.call_uuid ?? null,
      backendStatus: activeCall?.status ?? null,
      backendStatusReason: activeCall?.status_reason ?? null,
      backendHangupRequestStatus: activeCall?.hangup_request_status ?? null,
    });
    stateEnteredAtRef.current = enteredAt;
  }, [
    activeCall?.hangup_request_status,
    activeCall?.call_uuid,
    activeCall?.status,
    activeCall?.status_reason,
    state,
  ]);

  React.useEffect(() => {
    if (!activeCallContext?.callId || !session) {
      return;
    }

    syncCallDetails().catch(() => undefined);
  }, [activeCallContext?.callId, session, syncCallDetails]);

  React.useEffect(() => {
    if (
      !activeCallContext?.callId ||
      !activeCall ||
      !shouldPollCall(activeCall)
    ) {
      return;
    }

    const poller = setInterval(() => {
      syncCallDetails().catch(() => undefined);
    }, 4000);

    return () => clearInterval(poller);
  }, [activeCall, activeCallContext?.callId, syncCallDetails]);

  React.useEffect(() => {
    if (state === "outbound_ringing" || state === "incoming_ringing") {
      callAudioFeedback.playRingback().catch(() => {
        setAudioWarning("Enable sound to hear ringing.");
      });
      return;
    }

    callAudioFeedback.stopRingback();
  }, [state]);

  React.useEffect(() => {
    if (state !== "connected") {
      return;
    }

    const connectedSince = readConnectedSince(activeCall);
    if (!connectedSince) {
      setConnectedDurationSeconds(0);
      return;
    }

    const updateDuration = () => {
      const elapsed = Math.max(
        0,
        Math.floor((Date.now() - connectedSince) / 1000),
      );
      setConnectedDurationSeconds(elapsed);
    };

    updateDuration();
    const ticker = setInterval(updateDuration, 1000);

    return () => clearInterval(ticker);
  }, [activeCall, state]);

  React.useEffect(() => {
    if (state === "ended" || state === "error") {
      // no-op
    }
  }, [state]);

  React.useEffect(() => {
    return () => {
      callAudioFeedback.stopAll();
      resetInviteTracking();
      clearIncomingCallPrompt({ clearInvite: true });

      const client = signalWireClientRef.current;
      if (client) {
        disconnectSignalWireClientBestEffort(client, {
          attemptId: currentAttemptIdRef.current,
          callSid: currentCallSidRef.current,
        }).catch(() => undefined);
        signalWireClientRef.current = null;
      }

      activeInviteRef.current = null;
      activeCallSessionRef.current = null;
      mediaJoinConfirmedRef.current = false;
    };
  }, [clearIncomingCallPrompt, resetInviteTracking]);

  const contextValue = React.useMemo<TCallSessionContextValue>(
    () => ({
      state,
      stateLabel: describeSessionState(state),
      error,
      activeCall,
      activeCallContext,
      isStartingCall,
      isHangupSubmitting,
      isMuted,
      audioWarning,
      ringingAudioEnabled,
      connectedDurationSeconds,
      incomingCall,
      isIncomingActionSubmitting,
      startOutboundCall,
      answerIncomingCall,
      declineIncomingCall,
      requestHangup,
      toggleMute,
      dismissSummary,
      retryAudio,
      setRingingAudioEnabled,
    }),
    [
      state,
      error,
      activeCall,
      activeCallContext,
      isStartingCall,
      isHangupSubmitting,
      isMuted,
      audioWarning,
      ringingAudioEnabled,
      connectedDurationSeconds,
      incomingCall,
      isIncomingActionSubmitting,
      startOutboundCall,
      answerIncomingCall,
      declineIncomingCall,
      requestHangup,
      toggleMute,
      dismissSummary,
      retryAudio,
      setRingingAudioEnabled,
    ],
  );

  return (
    <CallSessionContext.Provider value={contextValue}>
      {children}
    </CallSessionContext.Provider>
  );
};

export const useCallSessionContext = (): TCallSessionContextValue => {
  const context = React.useContext(CallSessionContext);
  if (!context) {
    throw new Error("useCallSession must be used within CallSessionProvider.");
  }
  return context;
};

export const formatTerminalSummaryReason = (
  call: TCommunicationsCall | null,
): string | null => {
  if (!call) {
    return null;
  }

  const readableStatusReason = getReadableTerminalReason(call.status_reason);
  if (readableStatusReason) {
    return readableStatusReason;
  }

  return null;
};
